/**
 * eReg Sidebar Navigation Component
 */

import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  FolderTree,
  FileText,
  Upload,
  PenTool,
  Settings,
  X,
  ClipboardList,
} from 'lucide-react';
import { useEreg } from '../../contexts/EregContext';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
}

function NavItem({ to, icon, label, onClick }: NavItemProps) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `flex min-h-[44px] items-center gap-3 rounded-lg px-2.5 text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 lg:min-h-0 lg:py-1.5 ${
          isActive
            ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
            : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
        }`
      }
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  );
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { isAdmin, currentProtocol, siteMode } = useEreg();

  const handleNavClick = () => {
    // Close sidebar on mobile when navigation happens
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-white border-r border-gray-200 dark:bg-gray-800 dark:border-gray-700
          transform transition-transform duration-200 ease-in-out
          lg:transform-none
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Mobile header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 lg:hidden">
            <span className="font-semibold text-gray-900 dark:text-gray-100">Menu</span>
            <button
              onClick={onClose}
              className="p-1 rounded-lg text-gray-600 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <X size={20} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {/* Dashboard */}
            <NavItem
              to="/ereg/dashboard"
              icon={<LayoutDashboard size={18} />}
              label="Dashboard"
              onClick={handleNavClick}
            />

            {/* Protocol/Site specific navigation */}
            {(currentProtocol || siteMode) && (
              <>
                <div className="pt-4 pb-2">
                  <p className="px-3 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                    {siteMode ? 'Site Documents' : 'Study'}
                  </p>
                </div>

                <NavItem
                  to="/ereg/folders"
                  icon={<FolderTree size={18} />}
                  label="Folders"
                  onClick={handleNavClick}
                />

                <NavItem
                  to="/ereg/documents"
                  icon={<FileText size={18} />}
                  label="Documents"
                  onClick={handleNavClick}
                />

                <NavItem
                  to="/ereg/upload"
                  icon={<Upload size={18} />}
                  label="Upload"
                  onClick={handleNavClick}
                />

                {/* Protocol-only items */}
                {currentProtocol && (
                  <>
                    <NavItem
                      to="/ereg/signatures"
                      icon={<PenTool size={18} />}
                      label="Signatures"
                      onClick={handleNavClick}
                    />

                    <NavItem
                      to="/ereg/delegation"
                      icon={<ClipboardList size={18} />}
                      label="Delegation Log"
                      onClick={handleNavClick}
                    />
                  </>
                )}
              </>
            )}

            {/* Admin section */}
            {isAdmin && (
              <>
                <div className="pt-4 pb-2">
                  <p className="px-3 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                    Admin
                  </p>
                </div>

                <NavItem
                  to="/ereg/protocols"
                  icon={<FileText size={18} />}
                  label="Manage Studies"
                  onClick={handleNavClick}
                />
              </>
            )}

            {/* Settings */}
            <div className="pt-4 pb-2">
              <p className="px-3 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                Settings
              </p>
            </div>

            <NavItem
              to="/ereg/settings"
              icon={<Settings size={18} />}
              label="Preferences"
              onClick={handleNavClick}
            />
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
              Electronic Regulatory Binder
            </p>
          </div>
        </div>
      </aside>
    </>
  );
}
